// Team / Super Admin view — dark editor's-desk aesthetic.
// Sidebar of all clients · main panel switches by tab · super_admin can manage People.
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { LogOut, Plus, Search, UserCog, X, Copy, RefreshCw, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { usePortalAuth, PORTAL_API as API, formatApiError } from "./PortalAuth";
import { OverviewPanel, NotesPanel, TasksPanel, FilesPanel } from "./panels";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "notes",    label: "Messages" },
  { key: "tasks",    label: "Tasks" },
  { key: "files",    label: "Files" },
];

const initials = (name) =>
  (name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

export default function TeamView({ user }) {
  const { logout } = usePortalAuth();
  const [clients, setClients] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("overview");
  const [showPeople, setShowPeople] = useState(false);

  const isSuper = user.role === "super_admin";
  const selected = useMemo(() => clients.find((c) => c.id === selectedId) || null, [clients, selectedId]);

  async function loadClients() {
    try {
      const { data } = await axios.get(`${API}/clients`, { withCredentials: true });
      setClients(data);
      if (!selectedId && data.length) setSelectedId(data[0].id);
    } catch (e) { toast.error(formatApiError(e)); }
  }
  useEffect(() => { loadClients(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) =>
      (c.display_name || "").toLowerCase().includes(q) ||
      (c.user?.email || "").toLowerCase().includes(q) ||
      (c.tagline || "").toLowerCase().includes(q)
    );
  }, [clients, search]);

  return (
    <div className="min-h-screen bg-jet text-cream flex" data-testid="team-dashboard">
      {/* SIDEBAR */}
      <aside className="w-72 lg:w-80 shrink-0 border-r border-cream/10 flex flex-col h-screen sticky top-0">
        <div className="p-5 border-b border-cream/10 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/brand/mmedia-logo-light.png" alt="M Media" className="h-9 w-auto" draggable="false" />
            <span className="overline text-cream/55 hidden lg:block">Editor&apos;s Desk</span>
          </Link>
        </div>

        {/* Search */}
        <div className="px-5 pt-4 pb-3">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-cream/40" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search clients…"
              className="w-full bg-cream/5 border border-cream/10 focus:border-gold focus:bg-cream/10 outline-none pl-7 pr-3 py-2 text-sm transition-colors"
              data-testid="team-search"
            />
          </div>
        </div>

        {/* Client list */}
        <div className="overline text-cream/45 px-5 mb-2">Clients · {filtered.length}</div>
        <div className="flex-1 overflow-y-auto pb-4">
          {filtered.length === 0 && (
            <p className="text-cream/45 italic text-sm px-5 py-4">No clients yet.</p>
          )}
          {filtered.map((c) => {
            const active = c.id === selectedId;
            return (
              <button
                key={c.id}
                onClick={() => setSelectedId(c.id)}
                className={`w-full text-left px-5 py-3 border-l-2 flex items-center gap-3 transition-all ${
                  active ? "bg-cream/5 border-gold" : "border-transparent hover:bg-cream/5"
                }`}
                data-testid={`team-client-${c.id}`}
              >
                <div className={`h-9 w-9 rounded-full flex items-center justify-center text-xs ${active ? "bg-gold text-jet" : "bg-cream/10 text-cream"}`}>
                  {initials(c.display_name)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm truncate">{c.display_name}</div>
                  <div className="text-xs text-cream/50 truncate">{c.tagline || c.user?.email}</div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer actions */}
        <div className="border-t border-cream/10 p-4 space-y-2">
          {isSuper && (
            <button onClick={() => setShowPeople(true)} className="w-full overline border border-cream/20 hover:bg-cream/5 px-3 py-2 transition text-left" data-testid="team-manage-people">
              <UserCog size={13} className="inline mr-1.5" />Manage People
            </button>
          )}
          <div className="flex items-center justify-between gap-2 px-1">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{user.name}</div>
              <div className="text-xs text-cream/45 truncate capitalize">{user.role.replace("_", " ")}</div>
            </div>
            <button onClick={logout} className="overline border border-cream/20 hover:bg-carpet hover:border-carpet hover:text-cream px-2 py-1.5 transition" data-testid="portal-logout">
              <LogOut size={11} />
            </button>
          </div>
        </div>
      </aside>

      {/* MAIN */}
      <main className="flex-1 min-w-0">
        {/* Top bar */}
        <header className="border-b border-cream/10 px-6 lg:px-10 py-5 flex items-center justify-between gap-4 sticky top-0 bg-jet/95 backdrop-blur z-30">
          <div className="min-w-0">
            <div className="overline text-cream/45">Selected client</div>
            <h2 className="font-display text-2xl md:text-3xl uppercase truncate">
              {selected?.display_name || "—"}
            </h2>
          </div>
          {/* Tabs */}
          <div className="flex gap-2 overflow-x-auto" data-testid="team-tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`overline px-3 py-1.5 border whitespace-nowrap transition-all ${
                  tab === t.key ? "bg-gold text-jet border-gold" : "border-cream/20 hover:bg-cream/5"
                }`}
                data-testid={`team-tab-${t.key}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </header>

        <div className="px-6 lg:px-10 py-8">
          {!selected ? (
            <div className="text-center py-20 text-cream/55">
              <p className="font-serif italic text-2xl">No client selected.</p>
              {isSuper && (
                <button onClick={() => setShowPeople(true)} className="mt-6 overline border border-gold text-gold hover:bg-gold hover:text-jet px-4 py-2 transition" data-testid="team-add-first-client">
                  <Plus size={14} className="inline mr-1" /> Add your first client
                </button>
              )}
            </div>
          ) : (
            <>
              {tab === "overview" && (
                <OverviewPanel
                  client={selected}
                  currentUser={user}
                  tone="dark"
                  onSaved={(c) => setClients(clients.map((x) => (x.id === c.id ? { ...x, ...c } : x)))}
                />
              )}
              {tab === "notes" && <NotesPanel client={selected} currentUser={user} tone="dark" />}
              {tab === "tasks" && <TasksPanel client={selected} currentUser={user} tone="dark" />}
              {tab === "files" && <FilesPanel client={selected} currentUser={user} tone="dark" />}
            </>
          )}
        </div>
      </main>

      {/* MANAGE PEOPLE DRAWER */}
      {showPeople && isSuper && (
        <PeopleDrawer onClose={() => setShowPeople(false)} onClientsChanged={loadClients} />
      )}
    </div>
  );
}

// ────────────────────────────── People management drawer ──────────────────────────────
function PeopleDrawer({ onClose, onClientsChanged }) {
  const [people, setPeople] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newForm, setNewForm] = useState({ email: "", name: "", role: "client", display_name: "", tagline: "" });
  const [tempPwShown, setTempPwShown] = useState(null);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function load() {
    try {
      const { data } = await axios.get(`${API}/people`, { withCredentials: true });
      setPeople(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }
  useEffect(() => { load(); }, []);

  async function create(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...newForm, email: newForm.email.trim().toLowerCase() };
      const { data } = await axios.post(`${API}/people`, payload, { withCredentials: true });
      setTempPwShown({
        email: data.user.email,
        name: data.user.name,
        role: data.user.role,
        temp_password: data.temp_password,
      });
      setNewForm({ email: "", name: "", role: "client", display_name: "", tagline: "" });
      await load();
      onClientsChanged?.();
      toast.success("Account created. Share the credentials below.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  async function resetPw(p) {
    if (!window.confirm(`Generate a new temp password for ${p.email}?`)) return;
    try {
      const { data } = await axios.post(`${API}/people/${p.id}/reset-pw`, {}, { withCredentials: true });
      setTempPwShown({ email: p.email, name: p.name, role: p.role, temp_password: data.temp_password });
      toast.success("New password generated.");
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function deactivate(p) {
    if (p.role === "super_admin") { toast.error("You cannot deactivate the super admin."); return; }
    if (!window.confirm(`Deactivate ${p.email}?`)) return;
    try {
      await axios.delete(`${API}/people/${p.id}`, { withCredentials: true });
      toast.success("Deactivated.");
      await load();
      onClientsChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="people-drawer">
      <button className="flex-1 bg-jet/70 backdrop-blur-sm" onClick={onClose} aria-label="Close" />
      <div className="w-full md:w-[640px] h-full bg-jet border-l border-cream/10 overflow-y-auto">
        <div className="sticky top-0 bg-jet border-b border-cream/10 px-7 py-5 flex items-center justify-between z-10">
          <div>
            <div className="overline text-cream/45">Super Admin</div>
            <h3 className="font-display text-3xl uppercase mt-1">Manage People</h3>
          </div>
          <button onClick={onClose} className="text-cream/70 hover:text-cream"><X size={20} /></button>
        </div>

        {/* New person form */}
        <div className="px-7 py-6 border-b border-cream/10">
          <div className="overline text-cream/55 mb-4">Add a new client or team member</div>
          <form onSubmit={create} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="overline text-cream/55 text-xs">Email</label>
                <input type="email" required value={newForm.email} onChange={(e) => setNewForm({ ...newForm, email: e.target.value })}
                  className="w-full bg-transparent border-b border-cream/25 focus:border-gold outline-none py-2 mt-1" />
              </div>
              <div>
                <label className="overline text-cream/55 text-xs">Name</label>
                <input required value={newForm.name} onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
                  className="w-full bg-transparent border-b border-cream/25 focus:border-gold outline-none py-2 mt-1" />
              </div>
            </div>
            <div>
              <label className="overline text-cream/55 text-xs">Role</label>
              <select value={newForm.role} onChange={(e) => setNewForm({ ...newForm, role: e.target.value })}
                className="w-full bg-transparent border-b border-cream/25 focus:border-gold outline-none py-2 mt-1">
                <option className="bg-jet" value="client">Client</option>
                <option className="bg-jet" value="team">Team member</option>
              </select>
            </div>
            {newForm.role === "client" && (
              <>
                <div>
                  <label className="overline text-cream/55 text-xs">Display name (brand)</label>
                  <input value={newForm.display_name} onChange={(e) => setNewForm({ ...newForm, display_name: e.target.value })}
                    placeholder="e.g. Julia Post Jewelry"
                    className="w-full bg-transparent border-b border-cream/25 focus:border-gold outline-none py-2 mt-1" />
                </div>
                <div>
                  <label className="overline text-cream/55 text-xs">Tagline</label>
                  <input value={newForm.tagline} onChange={(e) => setNewForm({ ...newForm, tagline: e.target.value })}
                    placeholder="e.g. Fine jewelry · Newport Beach"
                    className="w-full bg-transparent border-b border-cream/25 focus:border-gold outline-none py-2 mt-1" />
                </div>
              </>
            )}
            <button type="submit" disabled={busy} className="overline border border-gold text-gold hover:bg-gold hover:text-jet px-4 py-2 transition" data-testid="people-create-btn">
              {busy ? "Creating…" : "Create account"}
            </button>
          </form>
        </div>

        {/* Temp password reveal */}
        {tempPwShown && (
          <div className="px-7 py-5 bg-gold/15 border-b border-gold/40">
            <div className="overline text-gold mb-2 flex items-center gap-2"><KeyRound size={13} />One-time credentials</div>
            <p className="text-cream text-sm">
              Send these to <strong>{tempPwShown.name}</strong> ({tempPwShown.email}) — they&apos;ll be asked to change the password on first sign-in.
            </p>
            <div className="mt-3 grid grid-cols-3 gap-3 items-end">
              <div>
                <div className="overline text-cream/60 text-[10px]">Email</div>
                <div className="font-mono text-sm">{tempPwShown.email}</div>
              </div>
              <div>
                <div className="overline text-cream/60 text-[10px]">Temp password</div>
                <div className="font-mono text-sm">{tempPwShown.temp_password}</div>
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`Email: ${tempPwShown.email}\nTemp password: ${tempPwShown.temp_password}\nPortal: ${window.location.origin}/portal`);
                  toast.success("Copied to clipboard.");
                }}
                className="overline border border-gold text-gold hover:bg-gold hover:text-jet px-3 py-2 transition"
              >
                <Copy size={12} className="inline mr-1" />Copy invite
              </button>
            </div>
            <button onClick={() => setTempPwShown(null)} className="overline text-cream/55 hover:text-cream mt-3 text-xs">Dismiss</button>
          </div>
        )}

        {/* People list */}
        <div className="px-7 py-6">
          <div className="overline text-cream/55 mb-3">All accounts · {people.length}</div>
          <div className="divide-y divide-cream/10 border-y border-cream/10">
            {people.map((p) => (
              <div key={p.id} className={`py-3 flex items-center gap-3 ${!p.is_active ? "opacity-50" : ""}`}>
                <div className="h-9 w-9 rounded-full bg-cream/10 flex items-center justify-center text-xs">{initials(p.name)}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{p.name}</div>
                  <div className="text-xs text-cream/55 truncate">
                    {p.email} · <span className="capitalize">{p.role.replace("_", " ")}</span>
                    {!p.is_active && " · inactive"}
                  </div>
                </div>
                {p.role !== "super_admin" && (
                  <div className="flex gap-1.5">
                    <button onClick={() => resetPw(p)} title="Reset password" className="p-2 border border-cream/15 hover:border-gold text-cream/70 hover:text-gold transition" data-testid={`people-reset-${p.id}`}>
                      <RefreshCw size={12} />
                    </button>
                    {p.is_active && (
                      <button onClick={() => deactivate(p)} title="Deactivate" className="p-2 border border-cream/15 hover:border-carpet text-cream/70 hover:text-carpet transition" data-testid={`people-deactivate-${p.id}`}>
                        <X size={12} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
